/**
 * The built-in **map-asset catalogue** (`decisions/0193`): set pieces a GM places on a map that
 * have a tile footprint and their own hit points — a giant scale tree, a fallen trunk.
 *
 * An asset is an ordinary `object` combatant spawned from an entry here (`spawnAsset` in
 * `@lancer-vtt/rules`), which stamps the entry's id on the combatant as `assetId`. **The mechanics
 * are snapshotted into the combatant's `base` at spawn**, as an NPC's are, so editing an entry
 * changes new spawns only, never an asset already on a map. The board art keys off `assetId`; the
 * combatant's name is the entry's plain `label`. This is the opposite of `TERRAIN_TILES` (`./tiles.ts`), where the tile is the live
 * source of a cell's mechanics: an object carries per-instance HP and statuses that a catalogue
 * edit could not rewrite coherently.
 *
 * A code constant rather than pack content for the same reason as the tile catalogue
 * (`decisions/0185` point 3): the engine stays pure, and pack content is frozen while
 * `typescript-pack-sdk` changes the format.
 *
 * The statlines are RAW's object defaults (Core p.58, p.68, p.87): 5 Evasion, 10 HP per Size
 * (`spawnObject` derives HP from Size), 1 Armor for tough wood, stone and steel. "Climbable" is passable at cost 2
 * on the 2D map. No asset blocks flight.
 */

import type { ObjectProfile } from "./model/combatantBase";

/** One catalogue entry. */
export type MapAsset = {
  /** The catalogue name: what the BUILD palette and the CLI list call this entry. Unique per entry. */
  name: string;
  /**
   * What a spawned asset is called on the board: the plain word a player would use ("Tree",
   * "Boulder"), shown on hover and in the roster. Several entries share one; the catalogue `name`
   * is what tells them apart when placing.
   */
  label: string;
  /** A short plain-words description of what the asset is. */
  blurb: string;
  /** Size of the asset, or of each section of a sectioned asset. HP is 10 per Size unless `hp` is set. */
  size: number;
  /** HP, where the developer ruled it apart from Size (the reactor: a Tier 1 Assault's 15). */
  hp?: number;
  evasion: number;
  armor: number;
  /** The spatial profile snapshotted onto the combatant, `rooted` and `remains` included. */
  objectProfile: ObjectProfile;
  /**
   * Present on an asset laid out as a line of independently destroyable sections sharing one
   * `objectGroupId` (the fallen trunk): the section counts a spawn may ask for, inclusive.
   */
  sections?: { min: number; max: number };
};

const DIFFICULT = 2;

/** The catalogue. Keys are the durable asset ids a combatant's `assetId` stores. */
export const MAP_ASSETS = {
  "scale-tree": {
    name: "Great tree",
    label: "Tree",
    blurb: "A towering scaled trunk, cut away at head height on this map. It blocks movement and sight, and falls into deadfall.",
    size: 3,
    evasion: 5,
    armor: 1,
    objectProfile: { blocksMovement: true, blocksSight: true, cover: "hard", rooted: true, remains: "deadfall" },
  },
  "seal-tree": {
    name: "Pillar tree",
    label: "Tree",
    blurb: "A ribbed column of a trunk, cut away at head height on this map. It blocks movement and sight, and falls into deadfall.",
    size: 2,
    evasion: 5,
    armor: 1,
    objectProfile: { blocksMovement: true, blocksSight: true, cover: "hard", rooted: true, remains: "deadfall" },
  },
  "horsetail-stand": {
    name: "Horsetail stand",
    label: "Plants",
    blurb: "A thicket of Calamites stems: slow to push through, impossible to see past.",
    size: 2,
    evasion: 5,
    armor: 0,
    objectProfile: { blocksSight: true, cover: "soft", moveCost: DIFFICULT, rooted: true },
  },
  "fallen-trunk": {
    name: "Fallen trunk",
    label: "Log",
    blurb: "A downed trunk lying in sections: slow to climb over, solid to hide behind.",
    size: 1,
    evasion: 5,
    armor: 0,
    objectProfile: { cover: "hard", moveCost: DIFFICULT, rooted: true },
    sections: { min: 3, max: 6 },
  },
  "hauler-wreck": {
    name: "Hauler wreck",
    label: "Wreck",
    blurb: "The burnt-out hulk of an industrial cargo hauler: hard cover, slow to climb across. Blown apart, it leaves loose debris.",
    size: 1,
    evasion: 5,
    armor: 0,
    // The RAW wreck profile (Core p.82), the same one `wreckConversion.ts` gives a destroyed mech's wreck.
    objectProfile: { cover: "hard", blocksMovement: false, moveCost: DIFFICULT, remains: "loose-debris" },
  },
  "standing-dead": {
    name: "Standing dead",
    label: "Dead tree",
    blurb: "A giant scale tree long dead and still standing, since nothing on Verdigris rots, cut away at head height. It blocks movement and sight, and falls into deadfall.",
    size: 3,
    evasion: 5,
    armor: 1,
    objectProfile: { blocksMovement: true, blocksSight: true, cover: "hard", rooted: true, remains: "deadfall" },
  },
  rockfall: {
    name: "Rockfall boulder",
    label: "Boulder",
    blurb: "A rockfall boulder: too big to climb, low enough to see over. Shattered, it leaves scree.",
    size: 2,
    evasion: 5,
    armor: 1,
    objectProfile: { blocksMovement: true, cover: "hard", rooted: true, remains: "scree" },
  },
  smelter: {
    name: "Smelter",
    label: "Furnace",
    blurb: "An arc smelter with an open crucible of molten metal. It blocks movement and sight; breached, it spills a lava field.",
    size: 3,
    evasion: 5,
    armor: 1,
    objectProfile: { blocksMovement: true, blocksSight: true, cover: "hard", rooted: true, remains: "lava-field" },
  },
  "mech-cradle": {
    name: "Mech cradle",
    label: "Mech frame",
    blurb: "A half-built mech lying in its assembly cradle: too big to cross, low enough to see over. Wrecked, it leaves wreckage.",
    size: 3,
    evasion: 5,
    armor: 1,
    objectProfile: { blocksMovement: true, cover: "hard", rooted: true, remains: "wreckage" },
  },
  "ore-cart": {
    name: "Ore cart",
    label: "Cart",
    blurb: "A steel mine wagon heaped with ore: hard cover, slow to climb across, and loose on its rails. Blown apart, it leaves loose debris.",
    size: 1,
    evasion: 5,
    armor: 1,
    objectProfile: { cover: "hard", moveCost: DIFFICULT, remains: "loose-debris" },
  },
  reactor: {
    name: "Reactor",
    label: "Reactor",
    blurb: "A foundry's fusion reactor in its armoured containment. It blocks movement and sight; breached, its molten core spills a lava field.",
    size: 5,
    // Developer, 2026-10-14: a Tier 1 Assault's HP and Armor, so a breach is in reach.
    hp: 15,
    evasion: 5,
    armor: 1,
    objectProfile: { blocksMovement: true, blocksSight: true, cover: "hard", rooted: true, remains: "lava-field" },
  },
  "mech-printer": {
    name: "Mech printer",
    label: "Mech printer",
    blurb: "A full-size mech printer, its bed empty: a gantry too big to cross, open enough to see through. Wrecked, it leaves wreckage.",
    size: 4,
    evasion: 5,
    armor: 1,
    objectProfile: { blocksMovement: true, cover: "hard", rooted: true, remains: "wreckage" },
  },
  "mech-printer-cataphract": {
    name: "Mech printer (Cataphract)",
    label: "Mech printer",
    blurb: "A full-size mech printer with a gold-armoured Cataphract lying finished on its bed. Wrecked, it leaves wreckage.",
    size: 4,
    evasion: 5,
    armor: 1,
    objectProfile: { blocksMovement: true, cover: "hard", rooted: true, remains: "wreckage" },
  },
  "mech-printer-everest": {
    name: "Mech printer (Everest)",
    label: "Mech printer",
    blurb: "A full-size mech printer with a gunmetal-and-gold Everest lying finished on its bed. Wrecked, it leaves wreckage.",
    size: 4,
    evasion: 5,
    armor: 1,
    objectProfile: { blocksMovement: true, cover: "hard", rooted: true, remains: "wreckage" },
  },
  "mech-printer-small": {
    name: "Small mech printer",
    label: "Printer",
    blurb: "A compact parts printer, a limb or a drone half-built on its bed: too big to cross, low enough to see over. Wrecked, it leaves wreckage.",
    size: 2,
    evasion: 5,
    armor: 1,
    objectProfile: { blocksMovement: true, cover: "hard", rooted: true, remains: "wreckage" },
  },
} satisfies Record<string, MapAsset>;

/** A catalogue asset id. */
export type MapAssetId = keyof typeof MAP_ASSETS;

/** Every catalogue id, in catalogue order. */
export const MAP_ASSET_IDS = Object.keys(MAP_ASSETS) as MapAssetId[];

/** Whether `id` names a catalogue asset. */
export function isMapAssetId(id: unknown): id is MapAssetId {
  return typeof id === "string" && Object.prototype.hasOwnProperty.call(MAP_ASSETS, id);
}

/**
 * The catalogue asset the board draws `c` as: its `assetId` when the catalogue knows it, else a
 * catalogue entry the object is recognised as by name. The name match serves objects placed before
 * their entry existed: the landing zone's `Hauler Wreck`s were spawned as plain objects, and this
 * gives them the hauler-wreck art without rewriting the world. It is render-side only; the rules
 * (the remains sweep, `rooted`) read `assetId` and nothing else.
 */
export function drawnAssetId(c: { kind: string; name: string; assetId?: string }): MapAssetId | undefined {
  if (isMapAssetId(c.assetId)) return c.assetId;
  if (c.kind !== "object" || c.assetId !== undefined) return undefined;
  return NAME_RECOGNISED[c.name.trim().toLowerCase()];
}

/** Catalogue entries a plain object is recognised as by its lower-cased name. */
const NAME_RECOGNISED: Record<string, MapAssetId> = { "hauler wreck": "hauler-wreck" };

/** The catalogue entry for `id`, or `undefined` for an id the catalogue doesn't know. */
export function mapAsset(id: string): MapAsset | undefined {
  return isMapAssetId(id) ? (MAP_ASSETS as Record<MapAssetId, MapAsset>)[id] : undefined;
}
